// Quattro AI — easy / medium / hard.
//
// Hard tier (Phase 1 upgrade — Feb 2026):
//  - countLine is correctly computed *through* the candidate cell (simulates
//    placement) so threats are not miscounted.
//  - Detects open-3 threats (forced-block patterns) on opponents.
//  - Detects fork creation (≥2 winning cells after a move) for both sides.
//  - 2-ply lookahead: maximise my position while minimising the worst
//    opponent reply threat through any currently-empty cell.
//  - Stronger eval (open-3 / closed-3 / open-2 / center control).
//  - Specials are used strategically: blocker saved for opponent open-3,
//    color_switcher prefers opponent cards sitting on their longest line.
import {
  legalNormalMoves,
  legalSpecialTargets,
  placeNormal,
  applySpecial,
  topCard,
  inBounds,
  canPlaceNormal,
  hasAnyPlacement,
  isAdjacentToAny,
} from "./quattro";

const DIRS = [[0, 1], [1, 0], [1, 1], [1, -1]];
const BOARD_N = 6;
const CENTER = 2.5;

// ------------------------------------------------------------------
// Line geometry helpers
// ------------------------------------------------------------------

// Walk from (r,c) in direction (dr,dc) for `owner` and return the number of
// consecutive cells (excluding the starting cell) whose TOP card is owned by
// `owner`. Stops at blocked / empty / foreign cells.
function walk(board, r, c, dr, dc, owner) {
  let n = 0;
  let nr = r + dr, nc = c + dc;
  while (inBounds(nr, nc)) {
    const t = topCard(board[nr][nc]);
    if (!t || t.owner !== owner) break;
    n++;
    nr += dr; nc += dc;
  }
  return n;
}

// After a HYPOTHETICAL placement at (r,c) by `owner`, what's the longest line
// through (r,c)?  (Counts (r,c) itself as part of the line.)
function lineThrough(board, r, c, owner) {
  let best = 1;
  for (const [dr, dc] of DIRS) {
    const f = walk(board, r, c, dr, dc, owner);
    const b = walk(board, r, c, -dr, -dc, owner);
    const len = 1 + f + b;
    if (len > best) best = len;
  }
  return best;
}

// For each direction through (r,c), also report how many "open ends" the line
// has — an open end is an in-bounds cell immediately past the line that is
// currently empty & playable (not blocked). Used to distinguish open-3 from
// closed-3 threats.
function directionalSegments(board, r, c, owner) {
  const segs = [];
  for (const [dr, dc] of DIRS) {
    const f = walk(board, r, c, dr, dc, owner);
    const b = walk(board, r, c, -dr, -dc, owner);
    const len = 1 + f + b;
    // open-end detection
    let openEnds = 0;
    const fr = r + dr * (f + 1), fc = c + dc * (f + 1);
    const br = r - dr * (b + 1), bc = c - dc * (b + 1);
    if (inBounds(fr, fc) && !board[fr][fc].blocked && !topCard(board[fr][fc])) openEnds++;
    if (inBounds(br, bc) && !board[br][bc].blocked && !topCard(board[br][bc])) openEnds++;
    segs.push({ len, openEnds });
  }
  return segs;
}

// Returns the set of empty cells where `owner` could place a card RIGHT NOW
// to make a 4-in-a-row. Used for fork detection and block-prioritisation.
// We don't know what card the opponent holds, so we assume any power suffices
// (worst-case threat analysis). Adjacency rule still applies.
function winningCellsFor(board, owner) {
  const out = [];
  for (let r = 0; r < BOARD_N; r++) {
    for (let c = 0; c < BOARD_N; c++) {
      const cell = board[r][c];
      if (cell.blocked) continue;
      // must be playable: stack must be empty (otherwise they'd need a higher
      // power, which we can't assume), or first move (but that can't win)
      if (cell.stack.length > 0) continue;
      // adjacency
      if (hasAnyPlacement(board) && !isAdjacentToAny(board, r, c)) continue;
      if (lineThrough(board, r, c, owner) >= 4) out.push([r, c]);
    }
  }
  return out;
}

// Score a full board from `me`'s perspective. Higher = better for me.
function evalBoard(board, me) {
  // Per-owner stats: best open/closed/double/length counts.
  const score = { 0: 0, 1: 0, 2: 0, 3: 0 };
  for (let r = 0; r < BOARD_N; r++) {
    for (let c = 0; c < BOARD_N; c++) {
      const top = topCard(board[r][c]);
      if (!top) continue;
      const segs = directionalSegments(board, r, c, top.owner);
      for (const { len, openEnds } of segs) {
        if (len >= 4) score[top.owner] += 10000;
        else if (len === 3 && openEnds >= 2) score[top.owner] += 400;  // open-3 ~ forced win
        else if (len === 3 && openEnds === 1) score[top.owner] += 60;
        else if (len === 2 && openEnds >= 2) score[top.owner] += 15;
        else if (len === 2) score[top.owner] += 4;
      }
      // center control
      const dist = Math.abs(r - CENTER) + Math.abs(c - CENTER);
      score[top.owner] += Math.max(0, 4 - dist) * 0.3;
    }
  }
  // Avoid double-counting: each length-L line contributes L times (once per
  // cell).  We don't need an exact normalisation because weights are ordinal.
  let oppMax = -Infinity;
  for (let p = 0; p < 4; p++) if (p !== me) oppMax = Math.max(oppMax, score[p]);
  return score[me] - oppMax;
}

// ------------------------------------------------------------------
// Specials — strategic targeting
// ------------------------------------------------------------------

function scoreSpecialTarget(state, card, r, c, me) {
  const board = state.board;
  const top = topCard(board[r][c]);
  const sp = card.special;

  if (sp === "power_up" && top) {
    // boost a card that sits on my longest line
    return lineThrough(board, r, c, me) * 12;
  }
  if (sp === "power_down" && top) {
    // neutralise an opponent card on their longest line AND with high power
    const ownerLine = lineThrough(board, r, c, top.owner);
    return ownerLine * 10 + (top.power || 0) * 0.5;
  }
  if (sp === "color_switcher" && top) {
    // flip opponent top — biggest win when their card is on a 3+ line
    const segs = directionalSegments(board, r, c, top.owner);
    const bestLen = Math.max(...segs.map(s => s.len));
    const openThreat = segs.some(s => s.len >= 3 && s.openEnds >= 2) ? 200 : 0;
    return bestLen * 18 + openThreat;
  }
  if (sp === "blocker") {
    // Ideal use: sit a blocker on an opponent's open-3 completion cell.
    let thr = 0;
    for (let p = 0; p < 4; p++) {
      if (p === me) continue;
      const segs = directionalSegments(board, r, c, p);
      for (const { len, openEnds } of segs) {
        if (len >= 3 && openEnds >= 2) thr = Math.max(thr, 300);
        else if (len >= 3) thr = Math.max(thr, 80);
        else if (len === 2 && openEnds >= 2) thr = Math.max(thr, 20);
      }
    }
    return thr;
  }
  return 0;
}

// ------------------------------------------------------------------
// Main decision
// ------------------------------------------------------------------

export function chooseAIMove(state, difficulty = "medium") {
  const card = state.drawnCard;
  if (!card) return null;
  const me = state.currentPlayer;

  // -------- SPECIAL CARDS --------
  if (card.special) {
    const targets = legalSpecialTargets(state, card);
    if (!targets.length) return null;
    // Easy: pure random target — no tactical scoring. Keeps casual win rates high.
    if (difficulty === "easy") {
      const [r, c] = targets[Math.floor(Math.random() * targets.length)];
      return { kind: "special", r, c };
    }
    let best = null, bestScore = -Infinity;
    for (const [r, c] of targets) {
      let s = scoreSpecialTarget(state, card, r, c, me);
      s += Math.random() * 0.5;
      if (s > bestScore) { bestScore = s; best = [r, c]; }
    }
    if (best) return { kind: "special", r: best[0], c: best[1] };
    return null;
  }

  const moves = legalNormalMoves(state, card);
  if (!moves.length) return null;

  // -------- EASY: random --------
  if (difficulty === "easy") {
    const [r, c] = moves[Math.floor(Math.random() * moves.length)];
    return { kind: "place", r, c };
  }

  // -------- Priority 1: winning move (medium + hard) --------
  for (const [r, c] of moves) {
    const next = placeNormal(state, r, c);
    if (next.winner === me) return { kind: "place", r, c };
  }

  // -------- Priority 2: block opponent immediate-win --------
  // For each opponent, compute the cells that would give them a 4-in-a-row
  // next turn and pick a move that defuses them.
  const oppWinCells = {};
  for (let p = 0; p < 4; p++) {
    if (p === me) continue;
    oppWinCells[p] = winningCellsFor(state.board, p);
  }
  for (const [r, c] of moves) {
    for (let p = 0; p < 4; p++) {
      if (p === me) continue;
      // Placing MY card on their winning cell defuses that specific threat.
      if (oppWinCells[p].some(([rr, cc]) => rr === r && cc === c)) {
        // If there's exactly one threat total across opponents, this blocks it.
        let totalThreats = 0;
        for (let q = 0; q < 4; q++) if (q !== me) totalThreats += oppWinCells[q].length;
        if (totalThreats <= 1) return { kind: "place", r, c };
      }
    }
  }

  // -------- MEDIUM: greedy extend with basic fork awareness --------
  if (difficulty === "medium") {
    let best = moves[0], bestScore = -Infinity;
    for (const [r, c] of moves) {
      const next = placeNormal(state, r, c);
      const myLine = lineThrough(next.board, r, c, me);
      const top = topCard(state.board[r][c]);
      const onTopBonus = top ? -1 : 0;
      const score = myLine * 10 + onTopBonus + Math.random();
      if (score > bestScore) { bestScore = score; best = [r, c]; }
    }
    return { kind: "place", r: best[0], c: best[1] };
  }

  // -------- HARD: 2-ply search with fork + threat detection --------
  let best = moves[0], bestScore = -Infinity;
  for (const [r, c] of moves) {
    const next = placeNormal(state, r, c);
    if (next.winner === me) return { kind: "place", r, c };

    // Base positional eval
    let score = evalBoard(next.board, me);

    // Fork bonus: how many of my cells now complete a 4-in-a-row?
    const myForkCells = winningCellsFor(next.board, me);
    if (myForkCells.length >= 2) score += 800;             // guaranteed win next turn
    else if (myForkCells.length === 1) score += 90;

    // Opponent forks we'd be ENABLING — huge penalty.
    for (let p = 0; p < 4; p++) {
      if (p === me) continue;
      const oppFork = winningCellsFor(next.board, p);
      if (oppFork.length >= 2) score -= 900;
      else if (oppFork.length >= 1) score -= 70;
    }

    // Stack exposure: placing a non-max power on an empty cell lets an
    // opponent stack higher next turn.  Penalty scaled by (9 - power).
    const wasEmpty = state.board[r][c].stack.length === 0;
    if (wasEmpty) score -= Math.max(0, 9 - card.power) * 0.9;

    // Steal bonus: stacking over an opponent's top is strong — it disrupts
    // their line AND places ours in the same cell.
    const topUnder = topCard(state.board[r][c]);
    if (topUnder && topUnder.owner !== me) score += 25;

    // Micro-tiebreak so identical scores don't always pick the same corner
    score += Math.random() * 0.7;

    if (score > bestScore) { bestScore = score; best = [r, c]; }
  }
  return { kind: "place", r: best[0], c: best[1] };
}

export function applyAIMove(state, move) {
  if (!move) return state;
  if (move.kind === "place") return placeNormal(state, move.r, move.c);
  if (move.kind === "special") return applySpecial(state, move.r, move.c);
  return state;
}

// Exported for unit tests.
export const _ai_internals = {
  lineThrough,
  directionalSegments,
  winningCellsFor,
  evalBoard,
  scoreSpecialTarget,
};

// Silence unused-import warning on canPlaceNormal (kept for potential future use)
void canPlaceNormal;
